import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import HomeHeroCarousel from "../components/HomeHeroCarousel";
import { useLang } from "../contexts/LangContext";
import { Button } from "../components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import BoxCard from "../components/BoxCard";
import WineCardB2C from "../components/WineCardB2C";
import ExperienceCard from "../components/ExperienceCard";
import TVEpisodeCard from "../components/TVEpisodeCard";
import NewsletterSignup from "../components/NewsletterSignup";
import { Sparkles, Wine, ArrowRight, Grape, Hotel, ChefHat, Tv, Crown, Briefcase, FileDown, Package, Globe } from "lucide-react";

const HERO = `${process.env.REACT_APP_BACKEND_URL}/api/static/tiles/hero_home.png`;

const COUNTRIES = [
  { code: "Italia", flag: "🇮🇹", label: "Italia" },
  { code: "Francia", flag: "🇫🇷", label: "Francia" },
  { code: "Spagna", flag: "🇪🇸", label: "Spagna" },
  { code: "Portogallo", flag: "🇵🇹", label: "Portogallo" },
  { code: "Germania", flag: "🇩🇪", label: "Germania" },
  { code: "USA", flag: "🇺🇸", label: "USA" },
  { code: "Argentina", flag: "🇦🇷", label: "Argentina" },
  { code: "Cile", flag: "🇨🇱", label: "Cile" },
  { code: "Australia", flag: "🇦🇺", label: "Australia" },
  { code: "Nuova Zelanda", flag: "🇳🇿", label: "Nuova Zelanda" },
  { code: "Sudafrica", flag: "🇿🇦", label: "Sudafrica" },
];

function SectionHeader({ kicker, title, subtitle, to, linkLabel, testid }) {
  const { t } = useLang();
  const label = linkLabel || t("home.see_all", "Vedi tutti");
  return (
    <div className="flex items-end justify-between mb-8 md:mb-10 gap-4">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold mb-2">{kicker}</div>
        <h2 className="editorial-heading text-3xl sm:text-4xl md:text-5xl leading-tight">{title}</h2>
        {subtitle && <p className="text-muted-foreground mt-2 text-sm md:text-base max-w-xl">{subtitle}</p>}
      </div>
      {to && (
        <Link to={to} data-testid={testid} className="hidden md:inline-flex items-center gap-2 text-sm hover:text-[hsl(var(--primary))] font-medium shrink-0">
          {label} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

function CarouselRow({ children }) {
  return (
    <Carousel opts={{ align: "start", loop: false }} className="w-full">
      <CarouselContent className="-ml-4">{children}</CarouselContent>
      <CarouselPrevious className="hidden md:flex" />
      <CarouselNext className="hidden md:flex" />
    </Carousel>
  );
}

export default function Home() {
  const { t } = useLang();
  const [boxes, setBoxes] = useState([]);
  const [wines, setWines] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [tv, setTv] = useState([]);

  useEffect(() => {
    api.get("/featured/wines?limit=8").then(r => setWines(r.data)).catch(() => {});
    api.get("/featured/boxes?limit=8").then(r => setBoxes(r.data)).catch(() => {});
    api.get("/experiences").then(r => setExperiences(r.data.slice(0, 8))).catch(() => {});
    api.get("/tv/episodes").then(r => setTv(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ===== HERO CAROUSEL — 6 slide AI animate (task 8b) ===== */}
      <HomeHeroCarousel />

      {/* ===== 4 SCELTE SEMPLICI (big tiles) ===== */}
      <section className="container-xl py-14 md:py-20">
        <div className="text-center mb-10 md:mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold mb-2">{t("home.tiles_eyebrow", "Come vuoi iniziare?")}</div>
          <h2 className="editorial-heading text-3xl md:text-5xl">{t("home.tiles_title", "Quattro strade, un solo indirizzo.")}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {[
            { to: "/vini", label: t("home.tile_wines", "Vini"), subtitle: t("home.tile_wines_sub", "1.200+ etichette"), icon: Wine, bg: "/api/static/tiles/tile_vini.png", testid: "tile-vini" },
            { to: "/box", label: t("home.tile_boxes", "Food Box"), subtitle: t("home.tile_boxes_sub", "Gourmet in 48h"), icon: Package, bg: "/api/static/tiles/tile_box.png", testid: "tile-box" },
            { to: "/esperienze", label: t("home.tile_experiences", "Esperienze"), subtitle: t("home.tile_experiences_sub", "Cantine & Resort"), icon: Hotel, bg: "/api/static/tiles/tile_esperienze.png", testid: "tile-esperienze" },
            { to: "/tv", label: t("home.tile_tv", "Amazonico TV"), subtitle: t("home.tile_tv_sub", "Dentro le cantine"), icon: Tv, bg: "/api/static/tiles/tile_tv.png", testid: "tile-tv" },
          ].map((tile) => (
            <Link key={tile.to} to={tile.to} data-testid={tile.testid} className="group relative aspect-[3/4] md:aspect-[4/5] overflow-hidden rounded-sm bg-black">
              <img src={tile.bg.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${tile.bg}` : tile.bg} alt={tile.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-black/20" />
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                <tile.icon className="h-6 w-6 text-[#E2A03F] mb-2" strokeWidth={1.5} />
                <div className="editorial-heading text-2xl md:text-3xl text-white leading-none">{tile.label}</div>
                <div className="text-xs text-white/60 mt-1 uppercase tracking-wider">{tile.subtitle}</div>
              </div>
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#E2A03F] text-black flex items-center justify-center opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all">
                <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== VINI BESTSELLER — carousel ===== */}
      <section className="py-14 md:py-20 bg-[hsl(var(--muted))]/30 border-y border-border">
        <div className="container-xl">
          <SectionHeader
            kicker={t("home.bestseller_eyebrow", "Vini · Bestseller")}
            title={t("home.bestseller_title", "Le etichette più richieste.")}
            subtitle={t("home.bestseller_sub", "Selezione editoriale aggiornata ogni settimana dall'AI in base a ciò che piace di più.")}
            to="/vini" testid="home-wines-all"
          />
          <CarouselRow>
            {wines.map((w) => (
              <CarouselItem key={w.id} className="pl-4 basis-[70%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                <WineCardB2C wine={w} />
              </CarouselItem>
            ))}
          </CarouselRow>
        </div>
      </section>

      {/* ===== AMAZONICO TV — carousel ===== */}
      <section className="py-14 md:py-20 bg-[#080808] text-white">
        <div className="container-xl mb-8 md:mb-10 flex items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] font-semibold mb-2 flex items-center gap-2">
              <Tv className="h-3.5 w-3.5" /> Amazonico TV · Stagione 1
            </div>
            <h2 className="editorial-heading text-3xl sm:text-4xl md:text-5xl leading-tight">
              Il vino <span className="italic text-[#C9A961]">si racconta</span>.
            </h2>
            <p className="text-white/60 mt-2 text-sm md:text-base max-w-xl">
              Reportage esclusivi dentro le cantine più iconiche. Produttori, terroir, vendemmie — nuovo episodio ogni 14 giorni.
            </p>
          </div>
          <Link to="/tv" data-testid="home-tv-all" className="hidden md:inline-flex items-center gap-2 text-sm text-[#C9A961] hover:text-white font-medium shrink-0">
            Tutti gli episodi <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="container-xl">
          <CarouselRow>
            {tv.map((ep) => (
              <CarouselItem key={ep.id} className="pl-4 basis-[80%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                <TVEpisodeCard ep={ep} />
              </CarouselItem>
            ))}
          </CarouselRow>
        </div>
      </section>

      {/* ===== FOOD BOX — carousel ===== */}
      <section className="py-14 md:py-20">
        <div className="container-xl">
          <SectionHeader
            kicker="Food Box · In vendita"
            title="Box gourmet dai marchi italiani."
            subtitle="Pasta, risotti, tiramisù, pizza, regionali. Tutto pronto in 48 ore con consegna refrigerata."
            to="/box" testid="home-boxes-all"
          />
          <CarouselRow>
            {boxes.map((b) => (
              <CarouselItem key={b.id} className="pl-4 basis-[75%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                <BoxCard box={b} />
              </CarouselItem>
            ))}
          </CarouselRow>
        </div>
      </section>

      {/* ===== ESPERIENZE — carousel ===== */}
      <section className="py-14 md:py-20 bg-[hsl(var(--muted))]/30 border-y border-border">
        <div className="container-xl">
          <SectionHeader
            kicker="Esperienze · Prenotabili ora"
            title="Cantine, vendemmie, wine resort."
            subtitle="Trasforma la passione per il vino in una vera esperienza — dalla Toscana all'Etna."
            to="/esperienze" testid="home-exp-all"
          />
          <CarouselRow>
            {experiences.map((e) => (
              <CarouselItem key={e.id} className="pl-4 basis-[80%] sm:basis-1/2 md:basis-1/3">
                <ExperienceCard exp={e} />
              </CarouselItem>
            ))}
          </CarouselRow>
        </div>
      </section>

      {/* ===== ESPLORA PER PAESE — country strip (compatto, sostituisce le 11 carousel) ===== */}
      <section className="py-14 md:py-20">
        <div className="container-xl text-center mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold mb-2">Il mondo in un catalogo</div>
          <h2 className="editorial-heading text-3xl md:text-5xl">Scegli il paese, scopri i vini.</h2>
        </div>
        <div className="container-xl grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-11 gap-3 md:gap-2">
          {COUNTRIES.map((c) => (
              <Link key={c.code} to={`/vini?paese=${encodeURIComponent(c.code)}`} data-testid={`country-${c.code}`} className="group aspect-square flex flex-col items-center justify-center border border-border rounded-sm hover:bg-[hsl(var(--primary))] hover:text-white hover:border-[hsl(var(--primary))] transition-colors">
              <div className="text-3xl md:text-4xl mb-1">{c.flag}</div>
              <div className="text-[10px] uppercase tracking-wider text-center px-1">{c.label}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== CLUB + B2B — side-by-side compact teaser ===== */}
      <section className="container-xl pb-14 md:pb-20 grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Club */}
        <Link to="/club" data-testid="home-club-tile" className="group relative overflow-hidden rounded-sm aspect-[16/10] md:aspect-[4/3] bg-[#0a0a0a]">
          <img src="https://images.pexels.com/photos/33749231/pexels-photo-33749231.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#7A1C2B]/70 via-black/20 to-black/25" />
          <div className="relative h-full flex flex-col justify-between p-8 md:p-10 text-white">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#C9A961]">
              <Crown className="h-3.5 w-3.5" /> Club Privilegia
            </div>
            <div>
              <h3 className="editorial-heading text-3xl md:text-4xl lg:text-5xl leading-tight mb-3">
                Il culto del vino, <span className="italic text-[#C9A961]">custodito.</span>
              </h3>
              <p className="text-white/80 text-sm mb-5 max-w-md">Eventi black-tie, viaggi, aste ex-cellar. 3 tier da €490/anno.</p>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C9A961] font-semibold">
                Scopri i livelli <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>

        {/* B2B */}
        <Link to="/b2b" data-testid="home-b2b-tile" className="group relative overflow-hidden rounded-sm aspect-[16/10] md:aspect-[4/3] bg-[#141112]">
          <img src="https://images.pexels.com/photos/5530546/pexels-photo-5530546.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/25 to-black/30" />
          <div className="relative h-full flex flex-col justify-between p-8 md:p-10 text-white">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#C4A661]">
              <Briefcase className="h-3.5 w-3.5" /> Wine Trade · B2B
            </div>
            <div>
              <h3 className="editorial-heading text-3xl md:text-4xl lg:text-5xl leading-tight mb-3">
                Il marketplace <span className="italic text-[#C4A661]">dei professionisti.</span>
              </h3>
              <p className="text-white/80 text-sm mb-5 max-w-md">Listini dinamici, AI Sommelier, preventivi PDF. Solo per buyer verificati.</p>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C4A661] font-semibold">
                Entra nel portale <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* ===== FRANCHISING — full-width hero teaser ===== */}
      <section data-testid="home-franchising-section" className="relative overflow-hidden bg-[#050505] text-white border-y border-[#C9A961]/20">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/tiles/franchising_storefront.png`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/30 to-black/15" />
        <div className="relative container-xl py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.5em] text-[#C9A961] mb-4 flex items-center gap-3">
              <Globe className="h-3.5 w-3.5" /> {t("nav.franchising", "Franchising")} · Network 2026
            </div>
            <h2 className="editorial-heading text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
              {t("section.franchising", "Apri il tuo Franchising")}<br /><span className="italic text-[#C9A961]">Amazonico 3000</span>
            </h2>
            <p className="mt-5 text-white/80 text-base md:text-lg max-w-lg">
              Un format consolidato. Un brand premium. Un team minimale.<br />Diventa franchisee della prima boutique italiana di vino, food box e gift d'eccellenza.
            </p>
            <div className="mt-7 grid grid-cols-3 gap-5 max-w-md">
              <div><div className="text-2xl md:text-3xl font-bold text-[#C9A961]">12</div><div className="text-[9px] uppercase tracking-[0.3em] text-white/50 mt-1">Store target 2026</div></div>
              <div><div className="text-2xl md:text-3xl font-bold text-[#C9A961]">28<span className="text-sm">m</span></div><div className="text-[9px] uppercase tracking-[0.3em] text-white/50 mt-1">Payback medio</div></div>
              <div><div className="text-2xl md:text-3xl font-bold text-[#C9A961]">30<span className="text-sm">gg</span></div><div className="text-[9px] uppercase tracking-[0.3em] text-white/50 mt-1">Operativi in</div></div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/franchising" data-testid="home-franchising-cta" className="inline-flex items-center gap-2 px-7 py-4 bg-[#C9A961] hover:bg-white text-black uppercase tracking-[0.25em] text-xs font-bold transition-colors">
                Richiedi informazioni <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/franchising#points" className="inline-flex items-center gap-2 px-6 py-4 border border-white/30 hover:border-[#C9A961] uppercase tracking-[0.25em] text-xs font-bold transition-colors">
                Punti di forza
              </Link>
            </div>
          </div>
          <div className="hidden md:grid grid-cols-2 gap-3">
            {["franchising_interior_wine", "franchising_food_display", "franchising_gift_corner", "franchising_storefront"].map((slug, i) => (
              <div key={slug} className={`relative aspect-square overflow-hidden ${i === 0 ? "translate-y-6" : i === 2 ? "-translate-y-6" : ""}`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/tiles/${slug}.png`} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MAGAZINE + REWARDS BANNER (task 4 + 6) ===== */}
      <section className="container-xl py-14 md:py-20 grid md:grid-cols-2 gap-4 md:gap-6" data-testid="home-magazine-rewards">
        {/* Magazine — anteprima Inverno 2026 N°1 */}
        <Link to="/rivista" data-testid="home-magazine-tile" className="group relative overflow-hidden rounded-sm aspect-[16/10] md:aspect-[4/3] bg-[#f5f1ea]">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/magazine/cover_winter-2026.png`} alt="Amazonico Magazine — Inverno 2026" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
          {/* Light gradient only at the bottom for text legibility */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />
          <div className="relative h-full flex flex-col justify-between p-6 md:p-8 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#C9A961] bg-black/40 backdrop-blur px-2.5 py-1 rounded-full">
                <FileDown className="h-3.5 w-3.5" /> Quarterly Magazine
              </div>
              <span className="px-2.5 py-1 bg-[#C9A961] text-black text-[9px] font-bold uppercase tracking-wider rounded-full">N°1 · Inverno</span>
            </div>
            <div className="max-w-md">
              <h3 className="editorial-heading text-2xl md:text-3xl lg:text-4xl leading-tight mb-2">
                {t("section.magazine_title", "Amazonico")} <span className="italic text-[#C9A961]">Magazine.</span>
              </h3>
              <p className="text-white/90 text-sm mb-4 hidden md:block">{t("section.magazine_sub", "La rivista trimestrale di vino, cucina e cultura italiana.")}</p>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C9A961] font-semibold group-hover:gap-3 transition-all">
                {t("section.magazine_cta", "Leggi i numeri")} <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>

        {/* Rewards — AI-generated still life */}
        <Link to="/punti" data-testid="home-rewards-tile" className="group relative overflow-hidden rounded-sm aspect-[16/10] md:aspect-[4/3] bg-[#3A050D]">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/rewards/rewards_card.png`} alt="Programma Punti Premio Amazonico" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
          {/* Light gradient only at the bottom for text legibility */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 via-black/30 to-transparent" />
          <div className="relative h-full flex flex-col justify-between p-6 md:p-8 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#C9A961] bg-black/40 backdrop-blur px-2.5 py-1 rounded-full">
                <Sparkles className="h-3.5 w-3.5" /> Programma Punti Premio
              </div>
              <span className="px-2.5 py-1 bg-[#C9A961] text-black text-[9px] font-bold uppercase tracking-wider rounded-full">1 € = 1 punto</span>
            </div>
            <div className="max-w-md">
              <h3 className="editorial-heading text-2xl md:text-3xl lg:text-4xl leading-tight mb-2">
                Ogni euro, <span className="italic text-[#C9A961]">un punto.</span>
              </h3>
              <p className="text-white/90 text-sm mb-4 hidden md:block">{t("section.rewards_sub", "Riscatta box gourmet, esperienze in cantina, weekend in resort.")}</p>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C9A961] font-semibold group-hover:gap-3 transition-all">
                {t("section.rewards_cta", "Scopri i premi")} <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* ===== NEWSLETTER ===== */}
      <NewsletterSignup variant="full" source="homepage" />

      {/* ===== PITCH DECK FOOTER ===== */}
      <section className="relative bg-gradient-to-br from-[#0a0a0a] via-[#1a0608] to-[#0a0a0a] text-white border-t border-[#C9A961]/20">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #C9A961 0%, transparent 40%), radial-gradient(circle at 85% 70%, #7A1C2B 0%, transparent 50%)" }} />
        <div className="relative container-xl py-14 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-3">Investitori · Stampa · Partner</div>
            <h2 className="editorial-heading text-3xl md:text-4xl leading-tight">Pitch deck disponibile.</h2>
            <p className="text-white/60 mt-2 max-w-md text-sm">7 pagine editoriali con dati live, unit economics, roadmap e ask.</p>
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pitch/download`} data-testid="home-pitch-download" className="inline-flex items-center gap-3 px-7 py-4 bg-[#C9A961] text-black font-semibold uppercase tracking-[0.25em] text-xs hover:bg-white transition-colors whitespace-nowrap">
            <FileDown className="h-4 w-4" /> Scarica PDF
          </a>
        </div>
      </section>
    </div>
  );
}
